import React, { useId } from 'react';
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import type { DotItemDotProps, TooltipPayloadEntry } from 'recharts';
import { scoreToColor } from '@/utils';
import styles from './TrendChart.module.scss';

const Y_TICKS = [0, 50, 100];
const DOT_RADIUS = 4;
const AXIS_TICK_FONT_SIZE = 11;
const AXIS_LABEL_OFFSET = -4;
const CHART_BOTTOM_MARGIN = 22;
const CHART_RIGHT_MARGIN = 12;
const AREA_FILL_TOP_OPACITY = 0.45;
const Y_AXIS_WIDTH = 28;

export const PLOTTED_MAX_VALUE = 100;

export interface ChartDatum {
  attempt: number;
  carried?: boolean;
  [dataKey: string]: number | boolean | null | undefined;
}

export interface SeriesDef {
  dataKey: string;
  label: string;
  color?: string;
  maxValue?: number;
}

const tooltipContentStyle: React.CSSProperties = {
  background: 'var(--color-bg-sidebar)',
  border: '1px solid var(--color-border-strong)',
  borderRadius: 'var(--radius-md)',
  fontSize: 'var(--font-size-xs)',
  boxShadow: 'var(--shadow-lg)',
};

interface TrendChartProps {
  data: ChartDatum[];
  series: SeriesDef[];
  height?: number;
}

export const TrendChart: React.FC<TrendChartProps> = ({ data, series, height = 100 }) => {
  const gradientPrefix = useId();
  const gradientId = (dataKey: string) => `${gradientPrefix}-${dataKey}`.replace(/[^\w-]/g, '');
  const seriesByDataKey = new Map(series.map((s) => [s.dataKey, s]));

  const renderDot = (seriesDef: SeriesDef) => (props: DotItemDotProps) => {
    const { cx, cy, payload, index } = props;
    const score = payload?.[seriesDef.dataKey];
    if (typeof score !== 'number') return null;
    const color = scoreToColor(score);
    const carried = !!payload.carried;
    return (
      <circle
        key={`dot-${seriesDef.dataKey}-${index}`}
        cx={cx}
        cy={cy}
        r={DOT_RADIUS}
        fill={carried ? 'var(--color-bg-elevated)' : color}
        stroke={color}
        strokeWidth={carried ? 2 : 0}
      />
    );
  };

  const xTicks = data.map((d) => d.attempt);
  const xDomain: [number, number] = [xTicks[0] ?? 0, xTicks[xTicks.length - 1] ?? 0];
  const axisMaxValue = series.length === 1 ? (series[0]?.maxValue ?? PLOTTED_MAX_VALUE) : PLOTTED_MAX_VALUE;
  const formatYTick = (value: number) => String(Math.round((value / PLOTTED_MAX_VALUE) * axisMaxValue));

  return (
    <div className={styles.reveal}>
      <ResponsiveContainer width="100%" height={height}>
        <AreaChart data={data} margin={{ top: 4, right: CHART_RIGHT_MARGIN, bottom: CHART_BOTTOM_MARGIN, left: 0 }}>
        <defs>
          {series.map((s) => (
            <linearGradient key={s.dataKey} id={gradientId(s.dataKey)} x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={s.color ?? 'var(--color-primary)'} stopOpacity={AREA_FILL_TOP_OPACITY} />
              <stop offset="95%" stopColor={s.color ?? 'var(--color-primary)'} stopOpacity={0} />
            </linearGradient>
          ))}
        </defs>
        <CartesianGrid stroke="var(--color-border-default)" vertical={false} />
        <XAxis
          dataKey="attempt"
          type="number"
          domain={xDomain}
          allowDecimals={false}
          ticks={xTicks}
          tickFormatter={(v: number) => `#${v}`}
          tick={{ fontSize: AXIS_TICK_FONT_SIZE, fill: 'var(--color-text-tertiary)' }}
          axisLine={{ stroke: 'var(--color-border-default)' }}
          tickLine={false}
          interval={0}
          label={{
            value: 'Attempts',
            position: 'insideBottom',
            offset: AXIS_LABEL_OFFSET,
            fill: 'var(--color-text-tertiary)',
            fontSize: AXIS_TICK_FONT_SIZE,
          }}
        />
        <YAxis
          domain={[0, PLOTTED_MAX_VALUE]}
          ticks={Y_TICKS}
          tickFormatter={formatYTick}
          tick={{ fontSize: AXIS_TICK_FONT_SIZE, fill: 'var(--color-text-tertiary)' }}
          axisLine={false}
          tickLine={false}
          width={Y_AXIS_WIDTH}
        />
        <Tooltip
          contentStyle={tooltipContentStyle}
          labelStyle={{ color: 'var(--color-text-secondary)' }}
          labelFormatter={(label) => `Attempt #${label}`}
          itemSorter={(item: TooltipPayloadEntry) => series.findIndex((s) => s.dataKey === item.dataKey)}
          formatter={(value, _name, entry: TooltipPayloadEntry) => {
            if (typeof value !== 'number') return null;
            const def = seriesByDataKey.get(String(entry.dataKey));
            const maxValue = def?.maxValue ?? PLOTTED_MAX_VALUE;
            const displayValue = Math.round((value / PLOTTED_MAX_VALUE) * maxValue);
            const carried = entry.payload?.carried;
            return [`${displayValue}/${maxValue}${carried ? ' (carried over)' : ''}`, def?.label ?? String(entry.dataKey)];
          }}
        />
        {series.map((s) => (
          <Area
            key={s.dataKey}
            type="linear"
            dataKey={s.dataKey}
            name={s.label}
            stroke={s.color ?? 'var(--color-primary)'}
            strokeWidth={2}
            fill={`url(#${gradientId(s.dataKey)})`}
            fillOpacity={1}
            dot={renderDot(s)}
            connectNulls={false}
            isAnimationActive={false}
          />
        ))}
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
};
